import { LogOut, Ship } from 'lucide-react';
import { Outlet, useNavigate } from 'react-router-dom';

import { useAuth } from '@/context/AuthContext';
import { displayErrorMessage } from '@/lib/error-display';
import { useBuildProject } from '@/lib/use-build-project';
import { useOfflineQueueSync } from '@/lib/use-offline-queue-sync';
import { useOfflineStatus } from '@/lib/use-offline-status';

import { SetupReadinessBanner } from '../setup/SetupReadinessBanner';

import { AppSidebar } from './AppSidebar';
import { OfflineStatusBadge } from './OfflineStatusBadge';

export function AppShell() {
  const { user, signOut } = useAuth();
  const { project, error: projectError } = useBuildProject();
  const { cloudStatus, pendingCount, failedCount, conflictCount } = useOfflineStatus();
  const navigate = useNavigate();
  useOfflineQueueSync(Boolean(user?.id));

  // Top nav shows the VESSEL (like every Navio app) — the product name lives at
  // the sidebar bottom. A new build's vessel identity is its hull no. (or the
  // shipyard until christened) — the same label the rest of the app uses.
  const vesselName = project
    ? (project.vesselName ??
      (project.hullNumber ? `Hull ${project.hullNumber}` : project.shipyardName))
    : 'New build';
  const subtitle = project
    ? [project.hullNumber ? project.shipyardName : null, project.buildPhase]
        .filter(Boolean)
        .join(' · ')
    : '';

  return (
    <div className="flex h-screen flex-col overflow-hidden bg-background text-foreground">
      {/* Slim utility header — project identity left, account right (NavioWorks pattern). */}
      <header className="flex h-11 shrink-0 items-center border-b border-border bg-background px-3">
        <button
          type="button"
          onClick={() => navigate('/')}
          className="flex min-w-0 items-center gap-2 transition-opacity hover:opacity-80"
        >
          <Ship className="h-4 w-4 shrink-0 text-foreground" />
          <span className="truncate text-base font-semibold tracking-tight">{vesselName}</span>
          {subtitle && (
            <span className="hidden truncate text-sm text-muted-foreground sm:inline" title={subtitle}>
              · {subtitle}
            </span>
          )}
        </button>

        <div className="flex-1" />

        <div className="flex items-center gap-2 text-muted-foreground">
          <OfflineStatusBadge />
          <span className="hidden max-w-[200px] truncate text-xs sm:inline" title={user?.email}>
            {user?.email}
          </span>
          <button
            type="button"
            onClick={() => signOut()}
            aria-label="Sign out"
            className="flex h-8 w-8 items-center justify-center rounded-md hover:bg-muted hover:text-foreground"
          >
            <LogOut className="h-4 w-4" />
          </button>
        </div>
      </header>

      {/* Domain sidebar + page surface. */}
      <div className="flex min-h-0 flex-1 overflow-hidden">
        <AppSidebar />
        <main className="min-w-0 flex-1 overflow-auto bg-muted/30">
          <div className="flex min-h-full flex-col px-4 py-3">
            {cloudStatus !== 'online' ? (
              <div className="mb-3 rounded-md border border-amber-300 bg-amber-50 px-3 py-2 text-sm text-amber-950">
                <span className="font-medium">
                  {cloudStatus === 'degraded' ? 'Cloud degraded' : 'Offline'}
                </span>
                <span className="ml-2">
                  Cached work remains visible. Pending changes will sync when the cloud is healthy.
                </span>
                {pendingCount > 0 ? (
                  <span className="ml-2 text-xs">
                    {pendingCount} pending, {failedCount} failed, {conflictCount} review.
                  </span>
                ) : null}
              </div>
            ) : null}
            <SetupReadinessBanner project={project} />
            {projectError ? (
              <div className="rounded-lg border border-destructive/40 bg-destructive/5 p-6 text-sm">
                <p className="font-medium text-destructive">Couldn’t load the build project</p>
                <p className="mt-1 text-muted-foreground">
                  {displayErrorMessage(projectError.message)}
                </p>
                <p className="mt-2 text-xs text-muted-foreground">
                  This is a load error, not an empty project — check your connection or vessel
                  access and try again.
                </p>
              </div>
            ) : (
              <Outlet />
            )}
          </div>
        </main>
      </div>
    </div>
  );
}
